import Box from '@mui/material/Box'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import { StatusDot } from './StatusIndicators'
import { formatAgo } from '../lib/formatTime'
import { NO_MOTION } from '../theme/animations'
import { MONO } from '../theme/appTheme'

/**
 * One user, one line of the table.
 *
 * `now` is passed in rather than read here so that every row on screen agrees
 * on what time it is — and so the whole table moves together, once a second.
 */
export default function RosterRow({ user, number, now }) {
  // Not a flag the server sends. `flashUntil` is a deadline we wrote on the row
  // when it last flipped; the row stays highlighted until the clock passes it.
  const flashing = user.flashUntil > now

  let flashColour = 'transparent'
  if (flashing) {
    flashColour = user.active ? 'custom.upFlash' : 'custom.downFlash'
  }

  return (
    <TableRow
      sx={{
        height: 44,
        fontSize: '0.84rem',
        transition: 'background-color 0.8s ease',
        bgcolor: flashColour,
        '& td': { borderBottom: '1px solid', borderColor: 'custom.lineSoft' },
        '&:hover': { bgcolor: 'custom.hover' },
        [NO_MOTION]: { transition: 'none' },
      }}
    >
      <TableCell
        sx={{
          pl: '12px',
          pr: 0,
          fontFamily: MONO,
          fontSize: '0.7rem',
          color: 'custom.ink3',
        }}
      >
        {number}
      </TableCell>

      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: '0.84rem', fontWeight: 600, letterSpacing: '-0.01em' }}
          >
            {user.user}
          </Typography>

        </Box>
      </TableCell>

      <TableCell
        sx={{
          fontSize: '0.84rem',
          color: 'custom.ink2',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {user.company}
      </TableCell>

      <TableCell>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: user.active ? 'custom.upText' : 'custom.downText',
          }}
        >
          <StatusDot active={user.active} />
          <span>{user.active ? 'Active' : 'Inactive'}</span>
        </Box>
      </TableCell>

      <TableCell>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: user.tallyActive ? 'custom.upText' : 'custom.downText',
          }}
        >
          <StatusDot active={user.tallyActive} />
          <span>{user.tallyActive ? 'Active' : 'Inactive'}</span>
        </Box>
      </TableCell>

      <TableCell
        sx={{
          textAlign: 'right',
          fontFamily: MONO,
          fontSize: '0.72rem',
          color: 'custom.ink2',
          whiteSpace: 'nowrap',
        }}
      >
        {user.active ? 'online ' : 'offline '}
        {formatAgo(now - user.changedAt)}
      </TableCell>
    </TableRow>
  )
}
